import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors, spacing, breakpoints } from '../../../constants/theme';
import { useAuth } from '../../../contexts/AuthContext';
import { useGetCreditTransactions } from '../../../hooks/useFirestoreQueries';

const CreditsTabContainer = styled.div`
  width: 100%;
`;

const NotificationMessage = styled.div<{ $type: 'success' | 'error' }>`
  background: ${props => props.$type === 'success' ? colors.success + '20' : colors.error + '20'};
  border: 1px solid ${props => props.$type === 'success' ? colors.success : colors.error};
  color: ${props => props.$type === 'success' ? colors.success : colors.error};
  padding: ${spacing.md};
  border-radius: 8px;
  margin-bottom: ${spacing.lg};
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  font-weight: 500;
  
  &::before {
    content: '${props => props.$type === 'success' ? '✅' : '❌'}';
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${spacing.lg};
  margin-bottom: ${spacing.xl};
  
  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${spacing.md};
  }
`;

const StatCard = styled.div`
  background: ${colors.background.secondary};
  border: 1px solid ${colors.background.tertiary};
  border-radius: 12px;
  padding: ${spacing.lg};
  text-align: center;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    border-color: ${colors.primary.main}40;
  }
`;

const StatIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: ${spacing.md};
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${colors.primary.main};
  margin-bottom: ${spacing.sm};
`;

const StatLabel = styled.div`
  color: ${colors.text.secondary};
  font-size: 0.9rem;
  font-weight: 500;
`;

const ActionSection = styled.div`
  background: ${colors.background.secondary};
  border: 1px solid ${colors.background.tertiary};
  border-radius: 12px;
  padding: ${spacing.xl};
  text-align: center;
`;

const ActionTitle = styled.h3`
  color: ${colors.text.primary};
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: ${spacing.md};
`;

const ActionDescription = styled.p`
  color: ${colors.text.secondary};
  margin-bottom: ${spacing.lg};
  line-height: 1.6;
`;

// Transaction History Components
const TransactionContainer = styled.div`
  background: ${colors.background.secondary};
  border: 1px solid ${colors.background.tertiary};
  border-radius: 12px;
  padding: ${spacing.xl};
  margin-top: ${spacing.xl};
`;

const TransactionTitle = styled.h3`
  color: ${colors.text.primary};
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: ${spacing.lg};
`;

const TransactionTable = styled.div`
  overflow-x: auto;
`;

const TransactionHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 100px 120px 150px 120px;
  gap: ${spacing.md};
  padding: ${spacing.md} 0;
  border-bottom: 2px solid ${colors.background.tertiary};
  font-weight: 600;
  color: ${colors.text.secondary};
  font-size: 0.9rem;
  
  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr 80px 100px 120px;
    gap: ${spacing.sm};
  }
`;

const TransactionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 100px 120px 150px 120px;
  gap: ${spacing.md};
  padding: ${spacing.md} 0;
  border-bottom: 1px solid ${colors.background.tertiary};
  align-items: center;
  
  &:hover {
    background: ${colors.background.tertiary}20;
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr 80px 100px 120px;
    gap: ${spacing.sm};
  }
`;

// Transaction Type Styling - Comprehensive handling for all transaction types:
// - 'adjustment': Primary blue (for admin credit adds/deducts)
// - 'usage': Red (for credit consumption)  
// - 'purchase'/'bonus': Green (for credit gains)
// - 'refund': Orange/warning (for refunded credits)
// - default: Gray (for unknown types)
const TransactionType = styled.span<{ $type: string }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    switch (props.$type) {
      case 'adjustment':
        return colors.primary.main + '20';
      case 'usage':
        return colors.error + '20';
      case 'purchase':
      case 'bonus':
        return colors.success + '20';
      case 'refund':
        return colors.warning + '20';
      default:
        return colors.background.tertiary;
    }
  }};
  color: ${props => {
    switch (props.$type) {
      case 'adjustment':
        return colors.primary.main;
      case 'usage':
        return colors.error;
      case 'purchase':
      case 'bonus':
        return colors.success;
      case 'refund':
        return colors.warning;
      default:
        return colors.text.secondary;
    }
  }};
`;

const TransactionAmount = styled.span<{ $type: string; $amount?: number }>`
  font-weight: 600;
  color: ${props => (props.$amount && props.$amount >= 0) ? colors.success : colors.error};
`;

const TransactionDescription = styled.span`
  color: ${colors.text.primary};
  font-size: 0.9rem;
`;

const TransactionDate = styled.span`
  color: ${colors.text.secondary};
  font-size: 0.85rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${spacing.xl};
  color: ${colors.text.secondary};
`;

interface CreditsTabProps {
  // Add any props needed for credits management
}

const CreditsTab: React.FC<CreditsTabProps> = () => {
  const { companyData } = useAuth();
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Get company data from AuthContext (already loaded)
  const companyId = companyData?.id;
  const currentCredits = companyData?.creditBalance || 0;

  // Fetch transaction history
  const { 
    data: transactionsData, 
    isLoading: isLoadingTransactions 
  } = useGetCreditTransactions(
    companyId || null,
    50,
    { enabled: !!companyId }
  );

  // Clear notification after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const transactions = transactionsData?.creditTransactions || [];
  
  // Calculate statistics from real data
  const thisMonthUsage = transactions
    .filter((t: any) => t.type === 'usage' && new Date(t.createdAt?.toDate ? t.createdAt.toDate() : t.createdAt).getMonth() === new Date().getMonth())
    .reduce((sum: number, t: any) => sum + Math.abs(t.amount), 0);
  
  const totalAdjustments = transactions
    .filter((t: any) => t.type === 'adjustment' && t.amount > 0)
    .reduce((sum: number, t: any) => sum + t.amount, 0);

  const stats = [
    {
      icon: '💎',
      value: !companyData ? '...' : currentCredits.toLocaleString(),
      label: 'Current Balance'
    },
    {
      icon: '📊',
      value: isLoadingTransactions ? '...' : thisMonthUsage.toLocaleString(),
      label: 'Used This Month'
    },
    {
      icon: '🎯',
      value: isLoadingTransactions ? '...' : totalAdjustments.toLocaleString(),
      label: 'Total Added'
    },
    {
      icon: '📈',
      value: isLoadingTransactions ? '...' : transactions.length.toString(),
      label: 'Total Transactions'
    }
  ];

  // Helper functions for transaction display
  const formatDate = (dateString: any) => {
    try {
      // Handle Firestore Timestamp objects
      const date = dateString?.toDate ? dateString.toDate() : new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  const formatAmount = (amount: number, type: string) => {
    const prefix = amount >= 0 ? '+' : '-';
    return `${prefix}${Math.abs(amount).toLocaleString()}`;
  };

  return (
    <CreditsTabContainer>
      {notification && (
        <NotificationMessage $type={notification.type}>
          {notification.message}
        </NotificationMessage>
      )}
      
      {!companyData ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div>Loading credits information...</div>
        </div>
      ) : (
        <>
          <StatsGrid>
            {stats.map((stat, index) => (
              <StatCard key={index}>
                <StatIcon>{stat.icon}</StatIcon>
                <StatValue>{stat.value}</StatValue>
                <StatLabel>{stat.label}</StatLabel>
              </StatCard>
            ))}
          </StatsGrid>
          
          <ActionSection>
            <ActionTitle>💎 Credit Management</ActionTitle>
            <ActionDescription>
              Credits are used for FlashCheck analyses, file processing, and other premium features. 
              Credits can be allocated by administrators through the platform management system.
            </ActionDescription>
            
            <div style={{ 
              padding: '2rem',
              background: colors.primary.main + '10',
              border: `2px dashed ${colors.primary.main}40`,
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚧</div>
              <div style={{ 
                fontSize: '1.5rem', 
                fontWeight: '600', 
                color: colors.primary.main,
                marginBottom: '0.5rem'
              }}>
                Feature Managed by Admins
              </div>
              <div style={{ 
                color: colors.text.secondary,
                fontSize: '0.9rem'
              }}>
                Credits are managed by platform administrators. Contact your administrator for credit allocation or questions about usage.
              </div>
            </div>
          </ActionSection>
          
          <TransactionContainer>
            <TransactionTitle>Recent Transactions</TransactionTitle>
            
            {isLoadingTransactions ? (
              <EmptyState>Loading transactions...</EmptyState>
            ) : transactions.length === 0 ? (
              <EmptyState>No transactions found</EmptyState>
            ) : (
              <TransactionTable>
                <TransactionHeader>
                  <div>Description</div>
                  <div>Type</div>
                  <div>Amount</div>
                  <div>Date</div>
                  <div>Balance After</div>
                </TransactionHeader>
                
                {transactions.slice(0, 10).map((transaction: any) => (
                  <TransactionRow key={transaction.id}>
                    <TransactionDescription>
                      {transaction.description || 'Credit transaction'}
                    </TransactionDescription>
                    
                    <TransactionType $type={transaction.type}>
                      {transaction.type}
                    </TransactionType>
                    
                    <TransactionAmount $type={transaction.type} $amount={transaction.amount}>
                      {formatAmount(transaction.amount, transaction.type)}
                    </TransactionAmount>
                    
                    <TransactionDate>
                      {formatDate(transaction.createdAt)}
                    </TransactionDate>
                    
                    <div style={{ color: colors.text.primary, fontWeight: '500' }}>
                      {transaction.balanceAfter?.toLocaleString() || '—'}
                    </div>
                  </TransactionRow>
                ))}
              </TransactionTable>
            )}
          </TransactionContainer>
        </>
      )}
    </CreditsTabContainer>
  );
};

export default CreditsTab;
